import { ImgHTMLAttributes, KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, Conversation, Message } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { BrandWordmark, HeroBg } from "@/components/Brand";

interface UIMessage extends Message {
  pending?: boolean;
}

// Lightweight "thinking" indicator shown while we're waiting for the LLM
// to emit its first streamed token. Three dots that pulse in sequence —
// gives the user visible feedback that the request is alive even when the
// first token takes 30+ seconds (gpt-5-mini reasoning latency). Switches
// to the streaming content as soon as a token arrives.
// Pick a friendly first-name-ish label from display_name (preferred — admin
// usually sets the human name there) or fall back to the local part of the
// email. Returns null if nothing usable so the caller can drop the greeting
// entirely instead of saying "Hi, ".
function greetingName(
  displayName: string | null,
  email: string | null
): string | null {
  const src = displayName?.trim() || email?.split("@")[0] || "";
  if (!src) return null;
  // For "Leonid Nesterov" → "Leonid"; for "leonid.nesterov" → "leonid".
  const first = src.split(/[\s._-]+/)[0] || src;
  // Capitalize first char, leave the rest as-is (so "iOS" stays "iOS").
  return first.charAt(0).toUpperCase() + first.slice(1);
}

function ThinkingIndicator() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 200);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="inline-flex items-center gap-2 text-ink-muted">
      <span className="thinking-dots">
        <span /><span /><span />
      </span>
      <span className="text-xs text-ink-dim">{elapsed}с</span>
    </span>
  );
}

// Markdown links to docs / external sites should open in a new tab so the
// chat thread isn't lost. Internal anchor jumps and javascript: URLs keep
// default behaviour. We also defensively set rel=noopener for any external
// link to deny window.opener access.
function MdLink({
  href,
  children,
  ...rest
}: { href?: string; children?: React.ReactNode } & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  if (!href) return <a {...rest}>{children}</a>;
  const isInPage = href.startsWith("#");
  if (isInPage) return <a href={href} {...rest}>{children}</a>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
}

// Split an assistant message into (body, sources) when it contains a
// "**Источники:**" / "Sources:" trailer with a contiguous "- ..." list.
// Returns { body: text-before, header: "Источники:", items: ["- foo", "- bar"] }
// or { body: original, items: [] } when no sources block is present.
function splitSourcesBlock(content: string): {
  body: string;
  header: string | null;
  items: string[];
} {
  const lines = content.split("\n");
  // Walk from the end: find the last list block, then the header line above it.
  let lastListEnd = -1;
  let lastListStart = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    const l = lines[i].trim();
    if (l === "" && lastListEnd === -1) continue; // trailing blanks
    if (/^[-*]\s+\S/.test(l)) {
      if (lastListEnd === -1) lastListEnd = i;
      lastListStart = i;
      continue;
    }
    break;
  }
  if (lastListStart === -1 || lastListEnd === -1) {
    return { body: content, header: null, items: [] };
  }
  // Look for a "Sources:" / "Источники:" header just before the list (allow
  // a single blank line separator).
  let headerIdx = -1;
  for (let i = lastListStart - 1; i >= 0 && i >= lastListStart - 2; i--) {
    const l = lines[i].trim();
    if (l === "") continue;
    // Match common variants: "Источники:", "**Источники:**", "**Источники**:".
    // Bold marks and colon can appear in either order around the word.
    if (/^\s*(?:\*\*)?(?:Источники|Sources):?\s*(?:\*\*)?:?\s*$/i.test(l)) {
      headerIdx = i;
      break;
    }
    break; // anything else stops the look-back
  }
  if (headerIdx === -1) {
    return { body: content, header: null, items: [] };
  }
  const body = lines.slice(0, headerIdx).join("\n").trimEnd();
  const headerText = lines[headerIdx].trim();
  const items = lines
    .slice(lastListStart, lastListEnd + 1)
    .filter((l) => l.trim() !== "");
  return { body, header: headerText, items };
}

const SOURCES_VISIBLE_BY_DEFAULT = 3;
const SOURCES_COLLAPSE_THRESHOLD = 5;

// Group conversations by exact calendar date (locale-formatted). Backend
// returns them sorted by updated_at desc, so we just preserve order while
// inserting into per-day buckets.
const _DATE_FMT = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function groupConversationsByDate(
  conversations: Conversation[]
): { label: string; items: Conversation[] }[] {
  const buckets = new Map<string, Conversation[]>();
  for (const c of conversations) {
    const d = new Date(c.updated_at);
    // YYYY-MM-DD as the bucket key (locale-stable), human label below.
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const arr = buckets.get(key);
    if (arr) arr.push(c);
    else buckets.set(key, [c]);
  }
  // Map preserves insertion order — same as descending by updated_at.
  const out: { label: string; items: Conversation[] }[] = [];
  for (const items of buckets.values()) {
    const label = _DATE_FMT.format(new Date(items[0].updated_at));
    out.push({ label, items });
  }
  return out;
}

const DATE_VISIBLE_BY_DEFAULT = 3;

function DateGroup({
  label,
  items,
  activeId,
  onActivate,
  onDelete,
}: {
  label: string;
  items: Conversation[];
  activeId: number | null;
  onActivate: (id: number) => void;
  onDelete: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const visible =
    items.length > DATE_VISIBLE_BY_DEFAULT && !open
      ? items.slice(0, DATE_VISIBLE_BY_DEFAULT)
      : items;
  const hiddenCount = items.length - visible.length;
  return (
    <div className="mb-3">
      <div className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-white">
        {label}
      </div>
      {visible.map((c) => (
        <div
          key={c.id}
          className={`group flex items-center justify-between rounded-md px-2 py-1.5 text-sm transition ${
            c.id === activeId
              ? "bg-bg-hover text-white"
              : "text-ink-muted hover:bg-bg-hover hover:text-white"
          }`}
        >
          <button
            onClick={() => onActivate(c.id)}
            className="flex-1 truncate text-left"
            title={c.title}
          >
            {c.title}
          </button>
          <button
            onClick={() => onDelete(c.id)}
            className="ml-1 opacity-0 group-hover:opacity-100 text-ink-dim hover:text-red-400"
            aria-label="Delete"
            title="Delete"
          >
            ✕
          </button>
        </div>
      ))}
      {(hiddenCount > 0 || open) && items.length > DATE_VISIBLE_BY_DEFAULT && (
        <button
          onClick={() => setOpen((v) => !v)}
          className="mt-0.5 px-2 text-[11px] text-ink-dim transition hover:text-white"
        >
          {open
            ? "▼ Скрыть"
            : `▶ Ещё ${hiddenCount} ${
                hiddenCount === 1
                  ? "чат"
                  : hiddenCount < 5
                    ? "чата"
                    : "чатов"
              }`}
        </button>
      )}
    </div>
  );
}

// Internal protocol markers used between rag.py and the query rewriter
// (so the rewriter can detect a disambiguation answer in conversation
// history). They MUST stay in the stored message content but are noise
// to the human reading the chat — strip them at display time only.
function stripInternalMarkers(text: string): string {
  return text
    .replace(/^<<VARIANTS>>\s*\n?/gm, "")
    .replace(/^<<END_VARIANTS>>\s*\n?/gm, "")
    .replace(/^<<AMBIGUITY>>[\s\S]*?<<END_AMBIGUITY>>\s*\n?/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function AssistantBody({ content }: { content: string }) {
  const cleaned = stripInternalMarkers(content);
  const { body, header, items } = splitSourcesBlock(cleaned);
  return (
    <div className="prose-msg">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{ img: AuthImg as any, a: MdLink as any }}
      >
        {body || content}
      </ReactMarkdown>
      {header && items.length > 0 && (
        <SourcesList header={header} items={items} />
      )}
    </div>
  );
}

function SourcesList({ header, items }: { header: string; items: string[] }) {
  const [open, setOpen] = useState(false);
  const visible =
    items.length > SOURCES_COLLAPSE_THRESHOLD
      ? items.slice(0, SOURCES_VISIBLE_BY_DEFAULT)
      : items;
  const hidden =
    items.length > SOURCES_COLLAPSE_THRESHOLD
      ? items.slice(SOURCES_VISIBLE_BY_DEFAULT)
      : [];
  const visibleMd = visible.join("\n");
  const hiddenMd = hidden.join("\n");
  return (
    <div className="mt-3 border-t border-bg-border pt-2">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{ a: MdLink as any }}
      >
        {header + "\n\n" + visibleMd}
      </ReactMarkdown>
      {hidden.length > 0 && (
        <>
          <button
            onClick={() => setOpen((v) => !v)}
            className="mt-1 text-xs text-ink-dim transition hover:text-white"
          >
            {open ? "▼" : "▶"} {open ? "Скрыть" : `Ещё ${hidden.length} ${
              hidden.length === 1 ? "источник" : hidden.length < 5 ? "источника" : "источников"
            }`}
          </button>
          {open && (
            <div className="mt-1">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{ a: MdLink as any }}
              >
                {hiddenMd}
              </ReactMarkdown>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Browser <img> doesn't send the Authorization header, so /page-images/*
// would 401. This component fetches the URL with the bearer token and
// renders the result as a blob URL.
function AuthImg({ src, ...rest }: { src?: string } & ImgHTMLAttributes<HTMLImageElement>) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const token = useAuthStore.getState().token;

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    if (!src) return;
    // Only auth-fetch /page-images/ — leave other URLs (data: URIs, http://) alone
    if (!src.startsWith("/page-images/")) {
      setBlobUrl(src);
      return;
    }
    fetch(src, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((b) => {
        if (cancelled) return;
        revoked = URL.createObjectURL(b);
        setBlobUrl(revoked);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      });
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [src, token]);

  if (!blobUrl) return null;
  return <img src={blobUrl} loading="lazy" className="my-2 max-h-64 cursor-pointer rounded border border-bg-border" onClick={() => window.open(blobUrl, "_blank")} {...rest} />;
}

export default function ChatPage() {
  const { email, displayName, isAdmin, logout } = useAuthStore();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const reloadConversations = useCallback(async () => {
    try {
      const list = await api.listConversations();
      setConversations(list);
      return list;
    } catch (x) {
      setErr((x as Error).message);
      return [];
    }
  }, []);

  useEffect(() => {
    reloadConversations();
  }, [reloadConversations]);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    (async () => {
      try {
        const msgs = await api.listMessages(activeId);
        setMessages(msgs);
      } catch (x) {
        setErr((x as Error).message);
      }
    })();
  }, [activeId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function onSend() {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setErr(null);

    const optimistic: UIMessage = {
      id: -Date.now(),
      role: "user",
      content: text,
      meta: null,
      created_at: new Date().toISOString(),
    };
    const placeholderId = -Date.now() - 1;
    const placeholder: UIMessage = {
      id: placeholderId,
      role: "assistant",
      content: "",
      meta: null,
      created_at: new Date().toISOString(),
      pending: true,
    };
    setMessages((m) => [...m, optimistic, placeholder]);

    // Typewriter effect: SSE delivers tokens in unpredictable chunks
    // (10-50 chars at once when the network flushes). Rendering them
    // immediately produces visible bursts. Instead, keep a "target" buffer
    // (whatever the SSE has delivered so far) and animate a "shown" cursor
    // toward it via requestAnimationFrame at a comfortable reading rate.
    //
    // Speed model: never slower than BASE_CPS (60 chars/sec — paced reading
    // speed). When backlog grows, accelerate so we always catch up within
    // CATCHUP_SEC. Result: end of long bursts is fully revealed within 1s
    // of the network delivering it, never lags more than that, never goes
    // slower than ~60 cps.
    const BASE_CPS = 40;
    const CATCHUP_SEC = 1.0;
    // The body (LLM-generated prose) goes through the typewriter. The
    // Sources/thumbs tail is appended INSTANTLY once it arrives —
    // typing it character-by-character causes the SourcesList collapse
    // toggle ("▶ Ещё N…" vs "▼ Скрыть") to flicker because the parser
    // recomputes the count as the markdown list grows mid-reveal.
    let bodyText = "";
    let tailText = "";
    let typedLen = 0;
    let streamComplete = false;
    let typewriterDone = false;
    let lastTick = 0;
    let resolveTypewriter: () => void = () => {};
    const typewriterFinished = new Promise<void>((res) => {
      resolveTypewriter = res;
    });

    const setBubbleContent = (text: string) => {
      setMessages((m) =>
        m.map((msg) =>
          msg.id === placeholderId ? { ...msg, content: text } : msg
        )
      );
    };

    const tick = (now: number) => {
      if (typewriterDone) return;
      const dt = lastTick ? now - lastTick : 16;
      lastTick = now;
      const backlog = bodyText.length - typedLen;

      if (backlog > 0) {
        // Catch-up: 500-char burst revealed within 1s; slow trickle
        // still types at BASE_CPS (~25ms/char) so the eye sees motion.
        const cps = Math.max(BASE_CPS, backlog / CATCHUP_SEC);
        const delta = Math.max(1, Math.round((cps * dt) / 1000));
        typedLen = Math.min(bodyText.length, typedLen + delta);
        setBubbleContent(bodyText.slice(0, typedLen) + tailText);
      } else if (streamComplete) {
        // Typewriter finished — final flush guarantees Sources/thumbs.
        setBubbleContent(bodyText + tailText);
        typewriterDone = true;
        resolveTypewriter();
        return;
      } else if (tailText) {
        // Body fully typed; tail already shown. Just keep ticking until
        // streamComplete fires (might happen before stream ends if
        // sources arrived mid-typing).
        setBubbleContent(bodyText + tailText);
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    const setBody = (next: string) => {
      bodyText = next;
    };
    const appendTail = (next: string) => {
      tailText += next;
    };

    let convId: number | null = activeId;
    let assistantMsgId: number | null = null;
    let finalMeta: Record<string, unknown> | null = null;

    try {
      await api.streamMessage(text, activeId ?? undefined, {
        onToken: (t) => setBody(bodyText + t),
        // Replace fires once at end of stream with the post-processed body
        // (strips [N] cites, model-emitted Sources block). Tail is unaffected.
        onReplace: (t) => setBody(t),
        // Sources/thumbs are appended instantly without typewriter — see
        // comment on bodyText/tailText above.
        onSources: (t) => appendTail(t),
        onThumbs: (t) => appendTail(t),
        onMeta: (m) => {
          convId = m.conversation_id;
          assistantMsgId = m.assistant_message_id;
          finalMeta = m as unknown as Record<string, unknown>;
        },
        onError: (e) => {
          throw new Error(e);
        },
      });
      // Network stream is done. Let the typewriter finish revealing what
      // it has buffered before we promote the bubble to a persisted msg
      // (which clears the pending state and stops the typewriter loop).
      streamComplete = true;
      await typewriterFinished;

      // Promote the placeholder to a real persisted message so feedback
      // buttons appear and subsequent reloads don't clobber the bubble.
      setMessages((m) =>
        m.map((msg) =>
          msg.id === placeholderId
            ? {
                ...msg,
                id: assistantMsgId ?? msg.id,
                pending: false,
                meta: finalMeta as any,
              }
            : msg
        )
      );
      if (convId !== null) {
        setActiveId(convId);
      }
      reloadConversations();
    } catch (x) {
      setErr((x as Error).message);
      setMessages((m) => m.filter((msg) => !msg.pending && msg.id > 0));
    } finally {
      // Stop the typewriter loop in all paths (success/error/cancel) so we
      // don't leave a runaway rAF after the bubble is gone.
      typewriterDone = true;
      resolveTypewriter();
      setSending(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }

  async function onDelete(id: number) {
    if (!confirm("Delete this conversation?")) return;
    await api.deleteConversation(id);
    if (activeId === id) {
      setActiveId(null);
      setMessages([]);
    }
    reloadConversations();
  }

  async function onFeedback(msgId: number, rating: 1 | -1) {
    try {
      await api.sendFeedback(msgId, rating);
      setMessages((m) =>
        m.map((x) =>
          x.id === msgId
            ? { ...x, meta: { ...(x.meta ?? {}), rating } }
            : x
        )
      );
    } catch (x) {
      setErr((x as Error).message);
    }
  }

  return (
    <div className="flex h-screen bg-bg text-ink">
      {/* Sidebar */}
      <aside className="flex w-72 flex-col border-r border-bg-border bg-bg-elevated">
        <div className="border-b border-bg-border px-4 py-4">
          <BrandWordmark />
          <Link
            to="/settings"
            className="mt-2 block text-[13px] text-ink-dim hover:text-white"
            title={email ?? ""}
          >
            <div className="truncate">{displayName || email}</div>
            <div className="mt-0.5 flex items-center gap-1.5 text-ink-dim">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3.5 w-3.5"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              <span>Settings (change password etc.)</span>
            </div>
          </Link>
        </div>
        <button
          onClick={() => {
            setActiveId(null);
            setMessages([]);
            setInput("");
          }}
          className="m-3 rounded-md border border-bg-border bg-bg-card px-3 py-2 text-sm font-medium text-white transition hover:border-accent hover:bg-bg-hover"
        >
          + New chat
        </button>
        <div className="flex-1 overflow-y-auto px-2 pb-2">
          {conversations.length === 0 && (
            <div className="px-2 py-4 text-xs text-ink-dim">
              No conversations yet.
            </div>
          )}
          {groupConversationsByDate(conversations).map((group) => (
            <DateGroup
              key={group.label}
              label={group.label}
              items={group.items}
              activeId={activeId}
              onActivate={setActiveId}
              onDelete={onDelete}
            />
          ))}
        </div>
        <div className="flex border-t border-bg-border text-xs">
          {isAdmin && (
            <Link
              to="/admin"
              className="flex-1 px-4 py-2.5 text-ink-muted hover:bg-bg-hover hover:text-white"
            >
              Admin
            </Link>
          )}
          <button
            onClick={logout}
            className="flex-1 px-4 py-2.5 text-left text-ink-muted hover:bg-bg-hover hover:text-white"
          >
            Log out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="relative flex flex-1 flex-col overflow-hidden bg-bg">
        {messages.length === 0 && (
          <>
            <HeroBg opacity={0.5} />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(10,10,12,0.55) 0%, rgba(10,10,12,0.85) 85%)",
              }}
            />
          </>
        )}

        <div className="relative z-10 flex-1 overflow-y-auto px-6 py-6">
          <div className="mx-auto max-w-3xl">
            {messages.length === 0 && (
              <div className="mt-24 text-center">
                <div className="inline-flex items-center gap-2 rounded-full border border-bg-border bg-bg-elevated/70 px-4 py-1.5 text-[11px] font-medium text-ink-muted backdrop-blur">
                  <span className="h-2 w-2 rounded-full bg-accent" />
                  FintegratorBot · Online
                </div>
                <div className="mt-6 font-display text-4xl font-semibold leading-tight">
                  {greetingName(displayName, email) && (
                    <>
                      Hi,{" "}
                      <span className="text-accent">
                        {greetingName(displayName, email)}
                      </span>
                      .{" "}
                    </>
                  )}
                  Ask about{" "}
                  <span className="text-accent">Temenos T24</span>
                </div>
                <div className="mt-3 text-sm text-ink-muted">
                  FUNDS.TRANSFER, AA, jBC, OFS, deployment — any topic, RU/EN.
                </div>
                <div className="mt-8 grid grid-cols-1 gap-2 text-left sm:grid-cols-2">
                  {[
                    "What is AA.ARRANGEMENT?",
                    "Чем отличается VERSION от ENQUIRY?",
                    "Как настроить CHECK.REC в FUNDS.TRANSFER?",
                    "Treasury: что делает Dealer desktop?",
                  ].map((q) => (
                    <button
                      key={q}
                      onClick={() => setInput(q)}
                      className="rounded-lg border border-bg-border bg-bg-elevated/70 px-3 py-2 text-sm text-white/85 backdrop-blur transition hover:border-accent hover:text-white"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} onFeedback={onFeedback} />
            ))}
            <div ref={bottomRef} />
          </div>
        </div>

        {err && (
          <div className="relative z-10 border-t border-red-500/30 bg-red-500/10 px-6 py-2 text-xs text-red-300">
            {err}
          </div>
        )}

        <div className="relative z-10 border-t border-bg-border bg-bg-elevated/80 px-6 py-4 backdrop-blur">
          <div className="mx-auto flex max-w-3xl items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask about T24… (Shift+Enter for newline)"
              rows={1}
              className="flex-1 resize-none rounded-md border border-bg-border bg-bg-card px-3 py-2 text-sm text-white placeholder:text-ink-dim"
              style={{ minHeight: 42, maxHeight: 180 }}
            />
            <button
              onClick={onSend}
              disabled={sending || !input.trim()}
              className="rounded-md bg-white px-5 py-2.5 text-sm font-semibold text-bg transition hover:bg-white/90 disabled:opacity-50"
            >
              {sending ? "…" : "Send"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

// Wraps the cost/latency badge with an admin gate. Regular users don't
// need to see token spend — it's an operations metric.
function AdminOnlyCostBadge({ meta }: { meta: Record<string, unknown> | null }) {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  if (!isAdmin) return null;
  return <MessageCostBadge meta={meta} />;
}

function MessageCostBadge({ meta }: { meta: Record<string, unknown> | null }) {
  if (!meta) return null;
  const cost = typeof meta.cost_usd === "number" ? (meta.cost_usd as number) : null;
  const latency =
    typeof meta.latency_ms === "number" ? (meta.latency_ms as number) : null;
  if (cost === null && latency === null) return null;
  const parts: string[] = [];
  if (cost !== null) {
    parts.push(cost < 0.001 ? `<$0.001` : `$${cost.toFixed(cost < 1 ? 4 : 2)}`);
  }
  if (latency !== null) parts.push(`${Math.round(latency)}ms`);
  return (
    <span
      className="ml-auto text-[10px] text-ink-dim"
      title="Generation cost and latency for this answer"
    >
      {parts.join(" · ")}
    </span>
  );
}

function MessageBubble({
  msg,
  onFeedback,
}: {
  msg: UIMessage;
  onFeedback: (id: number, rating: 1 | -1) => void;
}) {
  const isUser = msg.role === "user";
  const rating = (msg.meta as { rating?: number } | null)?.rating;
  return (
    <div className={`mb-5 flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
          isUser
            ? "bg-white text-bg"
            : "bg-bg-card border border-bg-border"
        }`}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
        ) : msg.pending && !msg.content ? (
          <ThinkingIndicator />
        ) : (
          <AssistantBody content={msg.content} />
        )}
        {!isUser && !msg.pending && msg.id > 0 && (
          <div className="mt-2 flex items-center gap-1 border-t border-bg-border pt-2">
            <button
              onClick={() => onFeedback(msg.id, 1)}
              className={`rounded p-1 text-xs transition hover:bg-bg-hover ${
                rating === 1 ? "bg-green-500/10 text-green-400" : "text-ink-dim"
              }`}
              title="Helpful"
            >
              👍
            </button>
            <button
              onClick={() => onFeedback(msg.id, -1)}
              className={`rounded p-1 text-xs transition hover:bg-bg-hover ${
                rating === -1 ? "bg-red-500/10 text-red-400" : "text-ink-dim"
              }`}
              title="Not helpful"
            >
              👎
            </button>
            <AdminOnlyCostBadge meta={msg.meta} />
          </div>
        )}
      </div>
    </div>
  );
}
